import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold",
    "rounded-[var(--radius-md)] transition-all duration-200",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-purple)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-base)]",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:size-4 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        primary:
          "bg-gradient-brand text-white shadow-[var(--shadow-glow-purple)] hover:shadow-[0_18px_60px_rgba(139,92,246,0.35)] hover:-translate-y-0.5 active:translate-y-0",
        secondary:
          "glass text-[var(--color-fg-primary)] hover:bg-[var(--color-bg-card-hover)] hover:border-[var(--color-border-accent)]",
        ghost:
          "bg-transparent text-[var(--color-fg-secondary)] hover:bg-[var(--color-bg-card)] hover:text-[var(--color-fg-primary)]",
        outline:
          "border border-[var(--color-border-strong)] bg-transparent text-[var(--color-fg-primary)] hover:border-[var(--color-brand-purple)] hover:bg-[var(--color-brand-purple-soft)]",
        danger:
          "bg-[var(--color-danger)] text-white hover:bg-[var(--color-danger)]/90",
        link:
          "text-[var(--color-brand-blue)] underline-offset-4 hover:underline px-0 h-auto",
      },
      size: {
        sm: "h-8 px-3 text-sm",
        md: "h-10 px-5 text-sm",
        lg: "h-12 px-7 text-base",
        icon: "size-10",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...rest }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...rest}
      />
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
